import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
    ArrowLeft, Search, Filter, TrendingUp, Users, Clock,
    ChevronDown, Loader2, ExternalLink, BarChart2
} from 'lucide-react';
import { fetchAllMarkets } from '../../services/marketService';
import { MarketDeepDive } from '../dashboard/MarketDeepDive';

const CATEGORIES = ['All', 'Crypto', 'Politics', 'Sports', 'Tech', 'Science', 'Economics', 'Stocks'];

export const AllMarketsPage = () => {
    const navigate = useNavigate();
    const [markets, setMarkets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('All');
    const [sortBy, setSortBy] = useState('volume');
    const [selectedMarket, setSelectedMarket] = useState(null);

    useEffect(() => {
        loadMarkets();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedCategory, sortBy]);

    const loadMarkets = async () => {
        setLoading(true);
        try {
            const data = await fetchAllMarkets({
                limit: 100, // Fetch more for better filtering/dedup
                category: selectedCategory,
                sortBy
            });

            // Deduplicate by slug
            const uniqueMarkets = [];
            const seenSlugs = new Set();

            data.forEach(m => {
                if (!seenSlugs.has(m.slug)) {
                    seenSlugs.add(m.slug);
                    uniqueMarkets.push(m);
                }
            });

            setMarkets(uniqueMarkets);
        } catch (error) {
            console.error('Failed to load markets:', error);
        }
        setLoading(false);
    };

    const filteredMarkets = markets.filter(m =>
        m.title.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
        <div className="min-h-screen bg-[#0b0c0f] text-white pt-20 pb-12 font-sans">
            <div className="fixed top-0 left-0 right-0 h-[2px] bg-secondary z-[60]" />

            <div className="container mx-auto px-4">
                {/* Header Section */}
                <div className="mb-10">
                    <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
                        <div>
                            <h1 className="text-4xl font-black tracking-tighter mb-2 italic">LIVE_MARKETS</h1>
                            <p className="text-muted-foreground text-xs font-mono uppercase tracking-[0.2em]">
                                REAL-TIME PREDICTION AGGREGATOR <span className="text-secondary">•</span> POLARIS PROTOCOL ACTIVE
                            </p>
                        </div>

                        <div className="flex gap-2 invisible sm:visible">
                            <div className="bg-white/5 border border-white/10 px-4 py-2 rounded-xl hidden sm:block">
                                <div className="text-[10px] text-muted-foreground uppercase font-bold">Active Markets</div>
                                <div className="text-xl font-black font-mono">{markets.length}</div>
                            </div>
                            <div className="bg-white/5 border border-white/10 px-4 py-2 rounded-xl">
                                <div className="text-[10px] text-muted-foreground uppercase font-bold">24h Vol</div>
                                <div className="text-xl font-black font-mono text-secondary">$1.2M</div>
                            </div>
                        </div>
                    </div>

                    <div className="mt-8 flex flex-col md:flex-row gap-4 items-center justify-between bg-white/[0.02] border border-white/10 p-3 rounded-2xl backdrop-blur-sm">
                        <div className="flex gap-2 overflow-x-auto pb-2 md:pb-0 w-full md:w-auto px-1">
                            {CATEGORIES.map(cat => (
                                <button
                                    key={cat}
                                    onClick={() => setSelectedCategory(cat)}
                                    className={`px-4 py-2 rounded-xl text-[10px] font-black tracking-widest transition-all whitespace-nowrap uppercase ${selectedCategory === cat
                                        ? 'bg-secondary text-black shadow-[0_0_15px_rgba(255,51,102,0.3)]'
                                        : 'bg-white/5 text-muted-foreground hover:bg-white/10 hover:text-white'
                                        }`}
                                >
                                    {cat}
                                </button>
                            ))}
                        </div>

                        <div className="relative w-full md:w-72">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                            <input
                                type="text"
                                placeholder="SEARCH_DATABASE..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full bg-black/40 border border-white/10 rounded-xl py-2.5 pl-10 pr-4 text-[10px] font-mono focus:outline-none focus:border-secondary/50 transition-all placeholder:text-white/10"
                            />
                        </div>
                    </div>
                </div>

                {/* Markets Grid */}
                {loading ? (
                    <div className="flex flex-col items-center justify-center py-32 gap-6">
                        <Loader2 className="w-12 h-12 text-secondary animate-spin" />
                        <div className="flex flex-col items-center gap-1">
                            <p className="text-muted-foreground font-mono text-[10px] tracking-[0.3em] animate-pulse">SYNCHRONIZING_POLARIS_CLOB</p>
                            <div className="w-32 h-[1px] bg-secondary/20 relative overflow-hidden">
                                <motion.div
                                    className="absolute inset-0 bg-secondary"
                                    animate={{ x: [-128, 128] }}
                                    transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}
                                />
                            </div>
                        </div>
                    </div>
                ) : filteredMarkets.length === 0 ? (
                    <div className="text-center py-32 border border-dashed border-white/10 rounded-3xl">
                        <p className="text-muted-foreground font-mono text-xs uppercase tracking-widest">No matching datasets found in sector: {selectedCategory}</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                        <AnimatePresence>
                            {filteredMarkets.map((market, idx) => (
                                <motion.div
                                    key={market.id}
                                    initial={{ opacity: 0, y: 30 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: idx * 0.05, ease: "easeOut" }}
                                    onClick={() => navigate(`/trade/${market.slug || market.id}`)}
                                    className="group relative bg-[#12141a]/40 border border-white/5 rounded-2xl overflow-hidden hover:border-secondary/40 transition-all cursor-pointer flex flex-col h-full shadow-lg hover:shadow-secondary/5"
                                >
                                    {/* Market Image Wrapper */}
                                    <div className="relative aspect-[16/10] overflow-hidden">
                                        <img
                                            src={market.image}
                                            alt={market.title}
                                            className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110 grayscale-[0.5] group-hover:grayscale-0"
                                            onError={(e) => {
                                                e.target.src = 'https://images.unsplash.com/photo-1611974765270-ca1258634369?q=80&w=400';
                                            }}
                                        />
                                        <div className="absolute inset-0 bg-gradient-to-t from-[#0b0c0f] via-transparent to-transparent opacity-80" />

                                        <div className="absolute top-3 left-3 flex gap-2">
                                            <div className="px-2 py-1 bg-black/60 backdrop-blur-md border border-white/10 rounded-md text-[9px] font-black text-white uppercase tracking-wider">
                                                {market.category}
                                            </div>
                                            <div className="px-2 py-1 bg-emerald-500/80 backdrop-blur-md rounded-md text-[9px] font-black text-black uppercase tracking-wider flex items-center gap-1">
                                                <div className="w-1 h-1 rounded-full bg-black animate-pulse" />
                                                Active
                                            </div>
                                        </div>

                                        <div className="absolute bottom-3 left-3 right-3">
                                            <h3 className="text-sm font-bold leading-tight line-clamp-2 group-hover:text-secondary transition-colors drop-shadow-lg">
                                                {market.title}
                                            </h3>
                                        </div>
                                    </div>

                                    {/* Content Area */}
                                    <div className="p-4 flex flex-col flex-grow bg-white/[0.01] space-y-3">
                                        <div className="flex justify-between items-center text-[10px] font-mono">
                                            <div className="text-muted-foreground uppercase tracking-tighter">Volume ({market.ticker || 'POLY'})</div>
                                            <div className="text-white font-bold">{market.volume}</div>
                                        </div>

                                        {/* AI Indicator: Expected Return (Consistency) */}
                                        <div className="flex justify-between items-center py-2 px-3 bg-secondary/10 rounded-lg border border-secondary/20">
                                            <span className="text-[9px] font-bold text-secondary uppercase tracking-widest">Est. Return</span>
                                            <span className="text-xs font-black text-emerald-400">
                                                +{market.agentScore ? Math.floor(market.agentScore * 1.2) : Math.floor((((parseFloat(market.long) || 50) * (parseFloat(String(market.volume).replace(/[^0-9.]/g, '')) || 100)) % 40) + 20)}%
                                            </span>
                                        </div>

                                        {/* Simplified Outcome Progress */}
                                        <div className="space-y-1">
                                            <div className="flex justify-between items-end text-[9px] font-mono mb-1">
                                                <span className="text-emerald-400 font-bold uppercase">Yes {market.long || 50}%</span>
                                                <span className="text-red-400 font-bold uppercase">No {100 - (market.long || 50)}%</span>
                                            </div>
                                            <div className="h-1.5 w-full bg-white/5 rounded-full overflow-hidden flex">
                                                <motion.div
                                                    initial={{ width: 0 }}
                                                    animate={{ width: `${market.long || 50}%` }}
                                                    className="h-full bg-emerald-500"
                                                />
                                                <motion.div
                                                    initial={{ width: 0 }}
                                                    animate={{ width: `${100 - (market.long || 50)}%` }}
                                                    className="h-full bg-red-500"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Action Hover */}
                                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-all duration-300">
                                        <div className="bg-secondary text-black px-4 py-2 rounded-full font-black text-[10px] tracking-widest flex items-center gap-2 shadow-2xl">
                                            ANALYS_DATA <TrendingUp className="w-3 h-3" />
                                        </div>
                                    </div>
                                </motion.div>
                            ))}
                        </AnimatePresence>
                    </div>
                )}
            </div>

            <AnimatePresence>
                {selectedMarket && (
                    <MarketDeepDive
                        market={selectedMarket}
                        onClose={() => setSelectedMarket(null)}
                    />
                )}
            </AnimatePresence>
        </div>
    );
};
